import {
  type NodeData,
  type NodeId,
  type Point,
  type Vector,
  point,
  snapToGrid,
  vector,
} from '@ipfabric/utils';
import { mapProp } from '@ipfabric/utils/fp/mapProp';
import { getNodeId } from 'components/Graph/GraphApp/GraphNode';
import type { DragNodesStepEventArgs } from 'components/Graph/GraphApp/events/GraphAppEvents';
import { createCurtain } from 'components/Graph/GraphApp/helpers/curtain';
import type { GraphRendererPlugin } from 'components/Graph/GraphApp/plugins/GraphRendererPlugin';
import { ZINDEX_NODE_DRAG_CURTAIN } from 'components/Graph/GraphApp/zIndexes';
import { indicateLongRender } from 'components/Graph/commands/rendering/indicateLongRendering';
import {
  type NodeMovePosition,
  setNodePositions,
} from 'components/Graph/commands/setNodePositions';
import {
  type DropPreview,
  createDropPreview,
} from 'components/Graph/helpers/dropPreview';
import {
  nodesDataFromIds,
  nodesDataFromParentIds,
} from 'components/Graph/model/GraphData';
import { flow } from 'fp-ts/lib/function';
import {
  type IPoint,
  type Point as PixiPoint,
  Ticker,
  type interaction,
} from 'pixi.js';

type InteractionEvent = interaction.InteractionEvent;

/**
 * How many pixels cursor has to move in a single direction to start dragging
 */
const DRAG_START_THRESHOLD = 5;

const AUTOSCROLL_EDGE_SIZE = 20;
const AUTOSCROLL_STEP = 10;

interface MovingNode {
  nodeId: NodeId;
  /**
   * Relative position of the node to the dragged node
   *
   * When dragging multiple nodes, one is pinned to mouse position
   * and others keep original relative position to it.
   * Therefore when dragging a single node, offset is always {0, 0}.
   */
  offset: Vector;
  originalPosition: Point;
}

type NodeMoveFullPosition = NodeMovePosition & { moveTo: Point };

interface StartDraggingArgs {
  initialScreenMousePosition: PixiPoint;
  movingNodeIds: NodeId[];
  onStep?: (args: DragNodesStepEventArgs) => void;
  onEnd?: () => void;
}

export const nodeDrag: GraphRendererPlugin = (pluginInterface) => {
  function startDragging({
    initialScreenMousePosition,
    movingNodeIds,
    onStep,
    onEnd,
  }: StartDraggingArgs) {
    // where the mouse was pressed down when dragging started
    const initialWorldMousePosition = pluginInterface.viewport.toWorld(
      initialScreenMousePosition,
    );
    const originalMovingNodesData = nodesDataFromIds(
      pluginInterface.getGraphData().nodes,
    )(movingNodeIds);
    const movingChildNodesData = nodesDataFromParentIds(
      pluginInterface.getGraphData().nodes,
    )(originalMovingNodesData);

    const movingNodesData = [
      ...originalMovingNodesData,
      ...movingChildNodesData,
    ];

    const createMovingNode = (boundNodeData: NodeData): MovingNode => ({
      nodeId: boundNodeData.id,
      offset: vector.fromAtoB(
        initialWorldMousePosition,
        boundNodeData.position,
      ),
      originalPosition: boundNodeData.position,
    });
    const originalMovingNodes = movingNodesData.map(createMovingNode);
    const movingNodes = [
      ...originalMovingNodes,
      ...movingChildNodesData.map(createMovingNode),
    ];

    let dropPreview: DropPreview | null = null;

    // move the node to maintain its position relatively to mouse position
    const moveByMousePosition = ({
      nodeId,
      offset,
      originalPosition,
    }: MovingNode): NodeMoveFullPosition => ({
      nodeId,
      moveTo: point.createOnVector(getWorldMousePosition(), offset),
      undo: originalPosition,
    });

    const { viewport } = pluginInterface;
    let moved = false;
    let screenMousePosition: PixiPoint = initialScreenMousePosition;
    const getWorldMousePosition = (): Point =>
      pluginInterface.viewport.toWorld(screenMousePosition);

    const movedOverThreshold = (currentPosition: IPoint) =>
      Math.abs(currentPosition.x - initialScreenMousePosition.x) >=
        DRAG_START_THRESHOLD ||
      Math.abs(currentPosition.y - initialScreenMousePosition.y) >=
        DRAG_START_THRESHOLD;
    const moveNode =
      (emitUpdate: boolean) =>
      ({ nodeId, moveTo }: NodeMoveFullPosition) => {
        const node = pluginInterface.nodeDisplayObjects.get(nodeId);
        if (node) {
          node.position.set(moveTo.x, moveTo.y);
          if (emitUpdate) {
            pluginInterface.events.emit('nodePositionChangeInSequence', {
              node,
            });
          }
        }
      };

    const dragToMousePosition = () => {
      const positions = movingNodes.map(moveByMousePosition);
      positions.forEach(moveNode(true));
      pluginInterface.requestRender();

      dropPreview?.move(positions);
    };

    const scrollOnAxis = (
      axis: 'x' | 'y',
      viewportSizeOnAxis: number,
    ): boolean => {
      const backwardFactor = Math.min(
        1,
        (AUTOSCROLL_EDGE_SIZE - screenMousePosition[axis]) /
          AUTOSCROLL_EDGE_SIZE,
      );
      if (backwardFactor > 0) {
        viewport[axis] += backwardFactor ** 2 * AUTOSCROLL_STEP;
        return true;
      }
      const forwardFactor = Math.min(
        1,
        (AUTOSCROLL_EDGE_SIZE +
          screenMousePosition[axis] -
          viewportSizeOnAxis) /
          AUTOSCROLL_EDGE_SIZE,
      );
      if (forwardFactor > 0) {
        viewport[axis] -= forwardFactor ** 2 * AUTOSCROLL_STEP;
        return true;
      }
      return false;
    };

    const autoscrollOnEdges = () => {
      const scrolledX = scrollOnAxis('x', viewport.screenWidth);
      const scrolledY = scrollOnAxis('y', viewport.screenHeight);
      if (scrolledX || scrolledY) {
        dragToMousePosition();
        pluginInterface.requestRender();
      }
    };

    const handleAppMouseMove = (event: InteractionEvent) => {
      if (!moved && movedOverThreshold(event.data.global)) {
        moved = true;
        pluginInterface.events.emit('interruptClick');
        pluginInterface.events.emit('nodeDragStart', {
          nodeIds: movingNodeIds,
        });
        Ticker.shared.add(autoscrollOnEdges);
        dropPreview = createDropPreview(
          movingNodesData.filter(
            (nodeData) =>
              !pluginInterface.getGraphData().hiddenNodes.has(nodeData.id),
          ),
          pluginInterface.nodeDisplayObjects,
          pluginInterface.requestRender,
        );
      }
      if (moved) {
        screenMousePosition = event.data.global.clone();
        dragToMousePosition();
        onStep?.({
          delta: vector.fromAtoB(
            initialWorldMousePosition,
            getWorldMousePosition(),
          ),
        });
      }
    };

    const handleWindowMouseUp = async () => {
      window.removeEventListener('mouseup', handleWindowMouseUp);
      pluginInterface.app.renderer.plugins.interaction.removeListener(
        'mousemove',
        handleAppMouseMove,
      );
      dropPreview?.remove();
      curtain.destroy();
      Ticker.shared.remove(autoscrollOnEdges);
      await pluginInterface.graphDispatch(indicateLongRender);
      if (moved) {
        // we must reset dragged nodes to original positions before firing setNodePositions,
        // because if target position after snapping to grid will not differ from initial position,
        // the node will not be re-drawn and rendered off-grid.
        movingNodes.forEach(
          flow(
            ({ nodeId, originalPosition }) => ({
              nodeId,
              moveTo: originalPosition,
            }),
            moveNode(false),
          ),
        );
        pluginInterface.graphDispatch(setNodePositions, {
          positions: originalMovingNodes.map(
            flow(moveByMousePosition, mapProp('moveTo', snapToGrid)),
          ),
        });
      }

      viewport.pause = false;
      onEnd?.();
      pluginInterface.events.emit('nodeDragEnd', { nodeIds: movingNodeIds });
    };

    const [curtain, drawCurtain] = createCurtain(pluginInterface);
    curtain.name = 'node-drag-curtain';
    curtain.zIndex = ZINDEX_NODE_DRAG_CURTAIN;
    pluginInterface.graphLayer.addChild(curtain);
    drawCurtain();

    pluginInterface.app.renderer.plugins.interaction.on(
      'mousemove',
      handleAppMouseMove,
    );
    window.addEventListener('mouseup', handleWindowMouseUp);

    // disable viewport dragging
    viewport.pause = true;
  }

  pluginInterface.events.addListener('nodeMouseDown', (e) => {
    if (!pluginInterface.isSelectionMode()) {
      const node = e.currentTarget;
      const initialScreenMousePosition = e.data.global.clone();
      const mainNodeId = getNodeId(node);

      const { selectedNodeIds } = pluginInterface.getGraphData();

      // if clicked current node is selected, move all selected nodes together,
      // otherwise move just that node
      const movingNodeIds = selectedNodeIds.has(mainNodeId)
        ? Array.from(selectedNodeIds)
        : [mainNodeId];
      startDragging({ initialScreenMousePosition, movingNodeIds });
    }
  });
  pluginInterface.events.addListener('dragNodes', (e) => {
    startDragging({
      initialScreenMousePosition: e.initialMousePosition,
      movingNodeIds: e.nodeIds,
      onStep: e.onDragStep,
      onEnd: e.onDragEnd,
    });
  });
};
